/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/shared/utils/core";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

const Tabs = TabsPrimitive.Root;

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  value?: string;
  onValueChange?: (value: string) => void;
  hideMobileDropdown?: boolean;
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, id, value, onValueChange, hideMobileDropdown = true, children, ...props }, ref) => {
    // Extract items from TabsTrigger children, including nested children
    const extractTriggers = (
      children: any, //React.ReactNode
    ): { value: string; node: React.ReactNode }[] => {
      return React.Children.toArray(children).flatMap((child) => {
        if (!React.isValidElement(child)) return [];

        // If it's a TabsTrigger, extract its info
        if ((child as any).type === TabsTrigger) {
          return [
            {
              value: (child as any).props.value,
              node: (child as any).props.children,
            },
          ];
        }

        // If it has children, recursively search them
        if ((child as any).props?.children) {
          return extractTriggers((child as any).props.children as any);
        }

        return [];
      });
    };

    const items = extractTriggers(children);
    const currentValue = value || items[0]?.value;

    return (
      <>
        {/* Mobile view - Select dropdown */}
        {!hideMobileDropdown && items.length > 0 ? (
          <div id={`${id}-mobile`} className="lg:hidden flex w-fit">
            <Select
              value={currentValue}
              onValueChange={(newValue) => {
                if (onValueChange) {
                  onValueChange(newValue);
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue>
                  {items.find((item) => item.value === currentValue)?.node || items[0]?.node}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {items.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.node}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {/* Desktop view - Regular tabs */}
        <TabsPrimitive.List
          ref={ref}
          className={cn(
            "w-full flex items-end justify-start gap-0 border-b border-stone-200 bg-transparent p-0 m-0",
            !hideMobileDropdown && "hidden lg:flex",
            className,
          )}
          id={`${id}-desktop`}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
      </>
    );
  },
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base styles
      "relative inline-flex items-center justify-center whitespace-nowrap",
      "pl-0 pr-0 mr-8 pb-3 text-sm font-normal data-[state=active]:font-medium",
      // Colors and states
      "text-stone-600 hover:text-stone-700 data-[state=active]:text-stone-900",
      "transition-colors",
      "focus-visible:outline-none",
      "disabled:pointer-events-none disabled:opacity-50",
      // Underline indicator - positioned to overlap the TabsList border
      "border-b-[2px] border-transparent hover:border-stone-300 data-[state=active]:border-stone-900",
      "-mb-[1px]", // Overlap with TabsList border
      "bg-transparent",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=inactive]:hidden",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
